import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useSelector } from "react-redux";
import {
  coerceAccessState,
  coerceStatus,
  coerceTier,
  getMySubscription,
  type MyBillingAccessState,
  type MySubscriptionView,
  type SubscriptionStatus,
  type SubscriptionTier,
} from "../api/General/Subscription";
import { track, AnalyticsEvents } from "../utils/analytics";

/**
 * 🔴 CO-227 — `DEFAULT_TRIAL_DAYS` IS GONE, AND `trialDays` WITH IT.
 *
 * The constant was 30 while every deployed environment granted 14 — it was already wrong, in all
 * four environments, before trial was retired. Retiring trial deletes the question rather than
 * re-syncing a number across the server config, this file and the marketing site's `TRIAL_DAYS`.
 *
 * ⚠️ The marketing site (mowazaf-website/src/data/pricing.ts) still advertises a trial. That is a
 * separate repo and a separate deploy; it is listed as a follow-up, not fixed here.
 */

/**
 * Subscription state used by the global Free/Trial banner and by every page
 * that branches on tier (dashboard, brochures, the route-level Free-tier lock).
 *
 * The codebase doesn't use TanStack Query yet, so this is a hand-rolled cache —
 * but, crucially, a *shared* one. The state lives at module scope behind
 * `useSyncExternalStore`, so all consumers read the SAME `data`/`isLoading`
 * instead of each spinning up its own `useState` + its own `/api/Subscription/me`
 * fetch. That sharing is what kills the "free-tier flash": before this was
 * shared, a Paid user landing on the dashboard would see `coerceTier(undefined)`
 * → "Free" → `FreeTierShowcase` for a frame while that component's *own* hook
 * instance re-fetched from null, even though MasterLayout had already resolved
 * the tier. With one shared store, by the time MasterLayout renders a child page
 * (it gates its Outlet on `data !== null`), the store is already resolved, so
 * `isFree`/`isPaid` are correct on first paint.
 *
 *   - 60s stale window (matches the spec) — we don't re-fetch within that window.
 *   - Refetch on window focus once the stale window has elapsed.
 *   - Re-fetch (and drop the previous user's cache) when the signed-in user
 *     changes (login/logout/role-swap).
 *
 * Returns a normalized shape with `isFree | isTrial | isPaid | isOperator`
 * booleans and a smart `daysRemaining` helper so callers don't have to repeat
 * the tier-dispatch logic. `data` is still exposed for the brochure overlay
 * which needs the actual TrialDaysRemaining number for its copy.
 */

const STALE_MS = 60_000;

export interface UseMySubscriptionResult {
  data: MySubscriptionView | null;
  isLoading: boolean;
  isError: boolean;
  /**
   * HTTP status of the failed /me call, or null when it has not failed (or the
   * failure had no response at all — network/timeout). The hook used to swallow
   * the reason entirely, so a Paid tenant getting the Free-tier paywall produced
   * ZERO telemetry and nothing to diagnose from.
   */
  errorStatus: number | null;
  refetch: () => Promise<void>;
  // Derived helpers — avoid having every consumer call coerceTier/coerceStatus.
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  isFree: boolean;
  isTrial: boolean;
  isPaid: boolean;
  isOperator: boolean;
  /** Trial days when Trial; paid days when Paid + non-null; otherwise null. */
  daysRemaining: number | null;
  /**
   * T8 — the tenant's billing access ladder, or null when the tenant is not billing-managed.
   * Available to EVERY authenticated role (unlike useBilling, which is Hr/Admin-only).
   */
  accessState: MyBillingAccessState | null;
  /** True when the tenant is billing-managed at all (accessState non-null). */
  isBillingManaged: boolean;
  /**
   * True when writes are blocked for payment reasons — Restricted or Dormant, matching
   * TierEnforcementMiddleware's own condition. Operators bypass every gate, so never restricted.
   */
  isWriteRestricted: boolean;
}

// ---------- Shared module-level store ----------
// One source of truth for the whole app. Components subscribe via
// useSyncExternalStore; mutations go through setStore so every consumer
// re-renders with the same snapshot.

interface SubscriptionStore {
  data: MySubscriptionView | null;
  isLoading: boolean;
  isError: boolean;
  /** HTTP status of the last failure, when there was a response to read it from. */
  errorStatus: number | null;
}

let store: SubscriptionStore = { data: null, isLoading: false, isError: false, errorStatus: null };
let lastFetchedAt = 0;
let cachedUserId: string | undefined;
let inFlight = false;
const listeners = new Set<() => void>();

function getSnapshot(): SubscriptionStore {
  return store;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function setStore(patch: Partial<SubscriptionStore>): void {
  store = { ...store, ...patch };
  listeners.forEach((l) => l());
}

async function loadSubscription(userId: string | undefined, force = false): Promise<void> {
  // Logged-out state — clear data and bail (mirrors the old hook's guard so a
  // transient logout doesn't fire a 401).
  if (!userId) {
    cachedUserId = undefined;
    lastFetchedAt = 0;
    if (store.data !== null || store.isLoading || store.isError) {
      setStore({ data: null, isLoading: false, isError: false, errorStatus: null });
    }
    return;
  }

  if (userId !== cachedUserId) {
    // Signed-in user changed — drop the previous user's cache so we never show
    // their tier, then fall through to fetch.
    cachedUserId = userId;
    lastFetchedAt = 0;
    setStore({ data: null, isLoading: false, isError: false, errorStatus: null });
  } else if (!force && store.data != null && Date.now() - lastFetchedAt < STALE_MS) {
    // Same user, fresh enough — honour the 60s stale window.
    return;
  }

  if (inFlight) return; // de-dup concurrent callers (several components mount together)
  inFlight = true;
  // Only surface the spinner on the very first load; background refreshes keep
  // the existing data visible.
  setStore({ isLoading: store.data == null, isError: false, errorStatus: null });
  try {
    const result = await getMySubscription();
    if (result.status < 400 && result.errors.length === 0) {
      lastFetchedAt = Date.now();
      setStore({ data: result.payload, isLoading: false, isError: false, errorStatus: null });
    } else {
      // Keep the status and report it. On this path `data` stays null, which
      // means coerceTier(undefined) === "Free" for every consumer — the exact
      // input that used to paywall a paying tenant. It must be observable.
      const status = typeof result.status === "number" ? result.status : null;
      setStore({ isLoading: false, isError: true, errorStatus: status });
      track(AnalyticsEvents.SubscriptionMeFailed, { status, reason: "result" });
    }
  } catch (e) {
    // Thrown rejections carry the status via requestHandling's `withHttpStatus`
    // shape when there was a response at all; a network/timeout failure has none.
    const status = (e as { status?: number } | null)?.status ?? null;
    setStore({ isLoading: false, isError: true, errorStatus: typeof status === "number" ? status : null });
    track(AnalyticsEvents.SubscriptionMeFailed, {
      status: typeof status === "number" ? status : null,
      reason: "throw",
    });
  } finally {
    inFlight = false;
  }
}

export function useMySubscription(): UseMySubscriptionResult {
  // The hook is mounted in MasterLayout which is itself behind PrivateRoute,
  // but we still gate on userId so a transient logout doesn't fire a 401.
  const userId = useSelector((state: { user?: { userId?: string } }) => state.user?.userId);

  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // Initial load + re-load on user change. Shared across instances — the first
  // mounted consumer triggers the fetch, the rest read the cached result.
  useEffect(() => {
    void loadSubscription(userId);
  }, [userId]);

  // Refetch on window focus once the stale window has elapsed.
  useEffect(() => {
    function onFocus() {
      if (Date.now() - lastFetchedAt >= STALE_MS) void loadSubscription(userId);
    }
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [userId]);

  const refetch = useCallback(() => loadSubscription(userId, true), [userId]);

  // ---------- Derived helpers ----------

  const { data, isLoading, isError, errorStatus } = snapshot;
  const tier = coerceTier(data?.tier);
  const status = coerceStatus(data?.status);
  const isOperator = !!data?.isOperator;
  const isFree = !isOperator && tier === "Free";
  const isTrial = !isOperator && tier === "Trial";
  const isPaid = !isOperator && tier === "Paid";

  let daysRemaining: number | null = null;
  if (data) {
    if (isTrial && typeof data.trialDaysRemaining === "number") {
      daysRemaining = data.trialDaysRemaining;
    } else if (isPaid && typeof data.paidDaysRemaining === "number") {
      daysRemaining = data.paidDaysRemaining;
    }
  }

  // T8 — access state, exposed to every role.
  const accessState = coerceAccessState(data?.accessState);
  const isBillingManaged = accessState !== null || !!data?.isBillingManaged;
  const isWriteRestricted =
    !isOperator && (accessState === "Restricted" || accessState === "Dormant");

  return {
    data,
    isLoading,
    isError,
    errorStatus,
    refetch,
    tier,
    status,
    isFree,
    isTrial,
    isPaid,
    isOperator,
    daysRemaining,
    accessState,
    isBillingManaged,
    isWriteRestricted,
  };
}
